import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "block text-sm font-medium text-gray-700 mb-2" }
const _hoisted_2 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_3 = { class: "border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors" }
const _hoisted_4 = ["accept", "id"]
const _hoisted_5 = ["for"]
const _hoisted_6 = { class: "text-sm text-gray-600" }
const _hoisted_7 = { class: "text-xs text-gray-500 mt-0.5" }
const _hoisted_8 = {
  key: 0,
  class: "text-xs text-gray-400 mt-0.5"
}
const _hoisted_9 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_10 = { class: "bg-gray-50 rounded-lg py-1 px-2 border border-gray-200" }
const _hoisted_11 = { class: "relative" }
const _hoisted_12 = ["src", "alt"]
const _hoisted_13 = { class: "text-sm text-gray-600" }
const _hoisted_14 = { class: "mb-1" }
const _hoisted_15 = { class: "mb-1" }
const _hoisted_16 = {
  key: 1,
  class: "mt-4"
}
const _hoisted_17 = { class: "bg-gray-50 rounded-lg p-4 border border-gray-200" }
const _hoisted_18 = { class: "flex items-center space-x-3" }
const _hoisted_19 = { class: "w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" }
const _hoisted_20 = { class: "flex-1 space-y-0.5" }
const _hoisted_21 = { class: "font-bold text-gray-900" }
const _hoisted_22 = { class: "text-sm text-gray-600" }
const _hoisted_23 = { class: "text-xs text-gray-500" }

import { ref, computed, watch } from 'vue';
import type { FileUploadFieldProps } from '@/types/sellerRegister';


export default /*@__PURE__*/_defineComponent({
  __name: 'FileUploadField',
  props: {
    label: {},
    required: { type: Boolean, default: false },
    accept: { default: '.pdf,.jpg,.jpeg,.png' },
    modelValue: {},
    fileTypes: { default: 'PDF, JPG, PNG' },
    uploadText: { default: 'Choisir un fichier' },
    gridClass: { default: '' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const fileName = ref('');
const uniqueId = computed(() => `file-upload-${Math.random().toString(36).substr(2, 9)}`);

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  
  if (target.files && target.files[0]) {
    const file = target.files[0];
    fileName.value = file.name;
    emit('update:modelValue', file);
  } else {
    fileName.value = '';
    emit('update:modelValue', null);
  }
};

// Vérifier si le fichier est une image
const isImageFile = (file: File | null): boolean => {
  if (!file) return false
  return file.type.startsWith('image/');
};

// Créer un aperçu de l'image
const getImagePreview = (file: File | null): string => {
  if (!file || !isImageFile(file)) return '';
  return URL.createObjectURL(file);
};

// Formater la taille du fichier
const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes || bytes === 0 || isNaN(bytes)) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Obtenir l'icône selon le type de fichier
const getFileIcon = (mimeType: string | undefined): string => {
  if (!mimeType) return 'fas fa-file';
  if (mimeType.startsWith('image/')) return 'fas fa-image';
  if (mimeType === 'application/pdf') return 'fas fa-file-pdf';
  if (mimeType.startsWith('text/')) return 'fas fa-file-alt';
  if (mimeType.startsWith('audio/')) return 'fas fa-file-audio';
  if (mimeType.startsWith('video/')) return 'fas fa-file-video';
  return 'fas fa-file';
};

// Supprimer le fichier
const removeFile = () => {
  fileName.value = '';
  emit('update:modelValue', null);
  
  // Réinitialiser l'input file pour permettre de re-sélectionner le même fichier
  const fileInput = document.getElementById(uniqueId.value) as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
};

// Ouvrir l'image en plein écran
const openImageFullscreen = () => {
  if (props.modelValue && isImageFile(props.modelValue)) {
    const url = URL.createObjectURL(props.modelValue);
    window.open(url, '_blank');
  }
};

// Initialiser avec la valeur du modèle
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    fileName.value = newValue.name;
  } else {
    fileName.value = '';
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_ctx.gridClass)
  }, [
    _createElementVNode("label", _hoisted_1, [
      _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
      (_ctx.required)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("input", {
        type: "file",
        accept: _ctx.accept,
        class: "hidden",
        id: uniqueId.value,
        onChange: handleFileChange
      }, null, 40, _hoisted_4),
      _createElementVNode("label", {
        for: uniqueId.value,
        class: "cursor-pointer"
      }, [
        _cache[0] || (_cache[0] = _createElementVNode("i", { class: "fas fa-cloud-upload-alt text-2xl text-gray-400 mb-1" }, null, -1)),
        _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.uploadText), 1),
        _createElementVNode("p", _hoisted_7, _toDisplayString(fileName.value || 'Aucun fichier choisi'), 1),
        (_ctx.fileTypes)
          ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Types acceptés : " + _toDisplayString(_ctx.fileTypes), 1))
          : _createCommentVNode("", true)
      ], 8, _hoisted_5)
    ]),
    (_ctx.modelValue && isImageFile(_ctx.modelValue))
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", { class: "flex items-center justify-between mb-3" }, [
              _cache[2] || (_cache[2] = _createElementVNode("h4", { class: "text-sm font-medium text-gray-800" }, "Aperçu du fichier", -1)),
              _createElementVNode("button", {
                onClick: removeFile,
                class: "text-red-500 hover:text-red-700 text-sm"
              }, _cache[1] || (_cache[1] = [
                _createElementVNode("i", { class: "fas fa-trash mr-1" }, null, -1),
                _createTextVNode("Supprimer ")
              ]))
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("img", {
                src: getImagePreview(_ctx.modelValue),
                alt: _ctx.modelValue?.name || '',
                class: "w-full max-w-l h-32 object-cover rounded-lg border border-gray-300"
              }, null, 8, _hoisted_12),
              _createElementVNode("button", {
                onClick: openImageFullscreen,
                class: "absolute inset-0 w-full h-full bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center text-white opacity-0 hover:opacity-100"
              }, _cache[3] || (_cache[3] = [
                _createElementVNode("i", { class: "fas fa-search-plus text-lg" }, null, -1)
              ]))
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("p", _hoisted_14, [
                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "font-bold" }, "Nom:", -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.modelValue?.name || 'N/A'), 1)
              ]),
              _createElementVNode("p", _hoisted_15, [
                _cache[5] || (_cache[5] = _createElementVNode("span", { class: "font-bold" }, "Taille:", -1)),
                _createTextVNode(" " + _toDisplayString(formatFileSize(_ctx.modelValue?.size)), 1)
              ]),
              _createElementVNode("p", null, [
                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "font-bold" }, "Type:", -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.modelValue?.type || 'N/A'), 1)
              ])
            ])
          ])
        ]))
      : (_ctx.modelValue && !isImageFile(_ctx.modelValue))
        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("div", { class: "flex items-center justify-between mb-3" }, [
                _cache[8] || (_cache[8] = _createElementVNode("h4", { class: "text-sm font-medium text-gray-800" }, "Fichier uploadé", -1)),
                _createElementVNode("button", {
                  onClick: removeFile,
                  class: "text-red-500 hover:text-red-700 text-sm"
                }, _cache[7] || (_cache[7] = [
                  _createElementVNode("i", { class: "fas fa-trash mr-1" }, null, -1),
                  _createTextVNode("Supprimer ")
                ]))
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("i", {
                    class: _normalizeClass([getFileIcon(_ctx.modelValue.type), "text-blue-600 text-xl"])
                  }, null, 2)
                ]),
                _createElementVNode("div", _hoisted_20, [
                  _createElementVNode("p", _hoisted_21, _toDisplayString(_ctx.modelValue?.name || 'N/A'), 1),
                  _createElementVNode("p", _hoisted_22, _toDisplayString(formatFileSize(_ctx.modelValue?.size)), 1),
                  _createElementVNode("p", _hoisted_23, _toDisplayString(_ctx.modelValue?.type || 'N/A'), 1)
                ])
              ])
            ])
          ]))
        : _createCommentVNode("", true)
  ], 2))
}
}

})